import {
    ClassDecl,
    Expr,
    JavaType,
    MethodDecl,
    Program,
    Stmt,
} from "./ast";

export class CodegenError extends Error {
    line: number;

    constructor(message: string, line: number) {
        super(message);
        this.line = line;
    }
}

type Scope = Map<string, JavaType>;

interface MethodSig {
    returnType: JavaType;
    paramTypes: JavaType[];
}

function promote(a: JavaType, b: JavaType): JavaType {
    if (a === "double" || b === "double") return "double";
    if (a === "float" || b === "float") return "double";
    if (a === "long" || b === "long") return "long";
    return "int";
}

function isNumericish(t: JavaType): boolean {
    return t === "int" || t === "long" || t === "float" || t === "double" || t === "char";
}

const WIDENING: Partial<Record<JavaType, JavaType[]>> = {
    char: ["char", "int", "long", "float", "double"],
    int: ["int", "long", "float", "double"],
    long: ["long", "float", "double"],
    float: ["float", "double"],
    double: ["double"],
    boolean: ["boolean"],
    String: ["String"],
};

function isAssignable(from: JavaType, to: JavaType): boolean {
    const allowed = WIDENING[from];
    return allowed ? allowed.includes(to) : from === to;
}

export class Codegen {
    private methods = new Map<string, MethodSig>();
    private lines: string[] = [];
    private currentReturnType: JavaType = "void";

    generate(program: Program): string {
        if (program.classes.length === 0) {
            throw new CodegenError("No class found in source", 1);
        }
        const cls = program.classes[0];
        this.collectSignatures(cls);

        const mainMethod = cls.methods.find((m) => m.name === "main");
        if (!mainMethod) {
            throw new CodegenError(`Class '${cls.name}' has no main method`, cls.line);
        }

        for (const method of cls.methods) {
            this.emitMethod(method);
        }

        this.lines.push("__step();");
        this.lines.push("main();");

        return this.lines.join("\n");
    }

    private collectSignatures(cls: ClassDecl) {
        for (const m of cls.methods) {
            this.methods.set(m.name, {
                returnType: m.returnType,
                paramTypes: m.params.map((p) => p.type),
            });
        }
    }

    private emitMethod(method: MethodDecl) {
        const scope: Scope = new Map();
        for (const p of method.params) scope.set(p.name, p.type);

        const previousReturnType = this.currentReturnType;
        this.currentReturnType = method.returnType;

        const params = method.params.map((p) => p.name).join(", ");
        this.lines.push(`function ${method.name}(${params}) {`);
        this.emitBlock(method.body, scope, 1);
        this.lines.push(`}`);

        this.currentReturnType = previousReturnType;
    }

    private indentStr(depth: number): string {
        return "  ".repeat(depth);
    }

    private emitBlock(stmts: Stmt[], scope: Scope, depth: number) {
        for (const stmt of stmts) {
            this.emitStmt(stmt, scope, depth);
        }
    }

    private requireBoolean(expr: Expr, scope: Scope, context: string): string {
        const t = this.inferType(expr, scope);
        if (t !== "boolean") {
            throw new CodegenError(
                `Incompatible types: ${t} cannot be converted to boolean (${context} condition)`,
                expr.line
            );
        }
        return this.genExpr(expr, scope);
    }

    private emitStmt(stmt: Stmt, scope: Scope, depth: number) {
        const ind = this.indentStr(depth);

        switch (stmt.kind) {
            case "VarDecl": {
                if (stmt.init) {
                    const value = this.coerceTo(stmt.init, stmt.varType, scope);
                    scope.set(stmt.name, stmt.varType);
                    this.lines.push(`${ind}let ${stmt.name} = ${value};`);
                } else {
                    scope.set(stmt.name, stmt.varType);
                    this.lines.push(`${ind}let ${stmt.name} = ${this.defaultValue(stmt.varType)};`);
                }
                return;
            }
            case "ExprStmt": {
                this.lines.push(`${ind}${this.genExpr(stmt.expr, scope)};`);
                return;
            }
            case "If": {
                const cond = this.requireBoolean(stmt.cond, scope, "if");
                this.lines.push(`${ind}if (${cond}) {`);
                this.emitBlock(stmt.then, new Map(scope), depth + 1);
                if (stmt.elseBranch) {
                    this.lines.push(`${ind}} else {`);
                    this.emitBlock(stmt.elseBranch, new Map(scope), depth + 1);
                }
                this.lines.push(`${ind}}`);
                return;
            }
            case "While": {
                const cond = this.requireBoolean(stmt.cond, scope, "while");
                this.lines.push(`${ind}while (${cond}) {`);
                this.lines.push(`${ind}  __step();`);
                this.emitBlock(stmt.body, new Map(scope), depth + 1);
                this.lines.push(`${ind}}`);
                return;
            }
            case "DoWhile": {
                this.lines.push(`${ind}do {`);
                this.lines.push(`${ind}  __step();`);
                this.emitBlock(stmt.body, new Map(scope), depth + 1);
                const cond = this.requireBoolean(stmt.cond, scope, "do-while");
                this.lines.push(`${ind}} while (${cond});`);
                return;
            }
            case "For": {
                const forScope = new Map(scope);
                const initCode = stmt.init ? this.genForInit(stmt.init, forScope) : "";
                const condCode = stmt.cond ? this.requireBoolean(stmt.cond, forScope, "for") : "";
                const updateCode = stmt.update ? this.genExprOnlyStmt(stmt.update, forScope) : "";
                this.lines.push(`${ind}for (${initCode}; ${condCode}; ${updateCode}) {`);
                this.lines.push(`${ind}  __step();`);
                this.emitBlock(stmt.body, new Map(forScope), depth + 1);
                this.lines.push(`${ind}}`);
                return;
            }
            case "Return": {
                if (stmt.expr) {
                    if (this.currentReturnType === "void") {
                        throw new CodegenError("A void method cannot return a value", stmt.line);
                    }
                    const value = this.coerceTo(stmt.expr, this.currentReturnType, scope);
                    this.lines.push(`${ind}return ${value};`);
                } else {
                    if (this.currentReturnType !== "void") {
                        throw new CodegenError(
                            `Missing return value; method must return ${this.currentReturnType}`,
                            stmt.line
                        );
                    }
                    this.lines.push(`${ind}return;`);
                }
                return;
            }
            case "Break":
                this.lines.push(`${ind}break;`);
                return;
            case "Continue":
                this.lines.push(`${ind}continue;`);
                return;
            case "Block":
                this.lines.push(`${ind}{`);
                this.emitBlock(stmt.body, new Map(scope), depth + 1);
                this.lines.push(`${ind}}`);
                return;
        }
    }

    private genForInit(stmt: Stmt, scope: Scope): string {
        if (stmt.kind === "VarDecl") {
            const value = stmt.init ? this.coerceTo(stmt.init, stmt.varType, scope) : this.defaultValue(stmt.varType);
            scope.set(stmt.name, stmt.varType);
            return `let ${stmt.name} = ${value}`;
        }
        if (stmt.kind === "ExprStmt") {
            return this.genExpr(stmt.expr, scope);
        }
        return "";
    }

    private genExprOnlyStmt(stmt: Stmt, scope: Scope): string {
        if (stmt.kind === "ExprStmt") return this.genExpr(stmt.expr, scope);
        return "";
    }

    private defaultValue(type: JavaType): string {
        switch (type) {
            case "int":
            case "long":
            case "double":
            case "float":
                return "0";
            case "boolean":
                return "false";
            case "char":
                return "0";
            case "String":
                return "null";
            default:
                return "null";
        }
    }

    private coerceTo(expr: Expr, targetType: JavaType, scope: Scope): string {
        const exprType = this.inferType(expr, scope);
        if (!isAssignable(exprType, targetType)) {
            throw new CodegenError(
                `Incompatible types: ${exprType} cannot be converted to ${targetType}`,
                expr.line
            );
        }
        const code = this.genExpr(expr, scope);
        if (exprType === "char" && targetType !== "char") {
            return `(${code}).charCodeAt(0)`;
        }
        return code;
    }

    inferType(expr: Expr, scope: Scope): JavaType {
        switch (expr.kind) {
            case "IntLiteral":
                return expr.isLong ? "long" : "int";
            case "DoubleLiteral":
                return "double";
            case "BooleanLiteral":
                return "boolean";
            case "CharLiteral":
                return "char";
            case "StringLiteral":
                return "String";
            case "Ident": {
                const t = scope.get(expr.name);
                if (!t) throw new CodegenError(`Cannot find variable '${expr.name}'`, expr.line);
                return t;
            }
            case "Assign": {
                const t = scope.get(expr.name);
                if (!t) throw new CodegenError(`Cannot find variable '${expr.name}'`, expr.line);
                return t;
            }
            case "Unary": {
                const t = this.inferType(expr.expr, scope);
                if (expr.op === "!") {
                    if (t !== "boolean") {
                        throw new CodegenError(`Bad operand type for '!': ${t}`, expr.line);
                    }
                    return "boolean";
                }
                if (!isNumericish(t)) {
                    throw new CodegenError(`Bad operand type for '${expr.op}': ${t}`, expr.line);
                }
                return t;
            }
            case "Logical": {
                const lt = this.inferType(expr.left, scope);
                const rt = this.inferType(expr.right, scope);
                if (lt !== "boolean" || rt !== "boolean") {
                    throw new CodegenError(
                        `Bad operand types for '${expr.op}': ${lt}, ${rt} (expected boolean)`,
                        expr.line
                    );
                }
                return "boolean";
            }
            case "Ternary": {
                const condType = this.inferType(expr.cond, scope);
                if (condType !== "boolean") {
                    throw new CodegenError(
                        `Incompatible types: ${condType} cannot be converted to boolean (ternary condition)`,
                        expr.line
                    );
                }
                this.inferType(expr.whenFalse, scope);
                return this.inferType(expr.whenTrue, scope);
            }
            case "Call": {
                const sig = this.methods.get(expr.callee);
                if (!sig) {
                    throw new CodegenError(`Cannot find method '${expr.callee}'`, expr.line);
                }
                if (sig.paramTypes.length !== expr.args.length) {
                    throw new CodegenError(
                        `Method '${expr.callee}' expects ${sig.paramTypes.length} argument(s) but got ${expr.args.length}`,
                        expr.line
                    );
                }
                expr.args.forEach((arg, idx) => {
                    const argType = this.inferType(arg, scope);
                    if (!isAssignable(argType, sig.paramTypes[idx])) {
                        throw new CodegenError(
                            `Incompatible types: ${argType} cannot be converted to ${sig.paramTypes[idx]} (argument ${idx + 1} of '${expr.callee}')`,
                            arg.line
                        );
                    }
                });
                return sig.returnType;
            }
            case "PrintCall":
                return "void";
            case "SystemInRef":
                throw new CodegenError(
                    "'System.in' can only be used as the argument to 'new Scanner(...)'",
                    expr.line
                );
            case "NewObject": {
                if (expr.className !== "Scanner") {
                    throw new CodegenError(
                        `Unsupported class '${expr.className}'. Only 'Scanner' is supported.`,
                        expr.line
                    );
                }
                if (expr.args.length !== 1 || expr.args[0].kind !== "SystemInRef") {
                    throw new CodegenError("Only 'new Scanner(System.in)' is supported", expr.line);
                }
                return "Scanner";
            }
            case "MethodCall": {
                const targetType = this.inferType(expr.target, scope);
                if (targetType !== "Scanner") {
                    throw new CodegenError(
                        `Cannot call method '${expr.method}' on type '${targetType}'`,
                        expr.line
                    );
                }
                const scannerReturnTypes: Partial<Record<string, JavaType>> = {
                    next: "String",
                    nextLine: "String",
                    nextInt: "int",
                    nextLong: "long",
                    nextDouble: "double",
                    nextBoolean: "boolean",
                    hasNext: "boolean",
                    hasNextInt: "boolean",
                    hasNextLine: "boolean",
                    close: "void",
                };
                const returnType = scannerReturnTypes[expr.method];
                if (!returnType) {
                    throw new CodegenError(`Unsupported Scanner method '${expr.method}'`, expr.line);
                }
                if (expr.args.length !== 0) {
                    throw new CodegenError(`Scanner.${expr.method}() takes no arguments`, expr.line);
                }
                return returnType;
            }
            case "Binary": {
                const lt = this.inferType(expr.left, scope);
                const rt = this.inferType(expr.right, scope);

                if (expr.op === "==" || expr.op === "!=") {
                    const bothNumeric = isNumericish(lt) && isNumericish(rt);
                    const bothBoolean = lt === "boolean" && rt === "boolean";
                    const bothString = lt === "String" && rt === "String";
                    if (!bothNumeric && !bothBoolean && !bothString) {
                        throw new CodegenError(`Incomparable types: ${lt} and ${rt}`, expr.line);
                    }
                    return "boolean";
                }

                if (expr.op === "<" || expr.op === "<=" || expr.op === ">" || expr.op === ">=") {
                    if (!isNumericish(lt) || !isNumericish(rt)) {
                        throw new CodegenError(`Bad operand types for '${expr.op}': ${lt}, ${rt}`, expr.line);
                    }
                    return "boolean";
                }

                if (expr.op === "+") {
                    if (lt === "String" || rt === "String") return "String";
                    if (!isNumericish(lt) || !isNumericish(rt)) {
                        throw new CodegenError(`Bad operand types for '+': ${lt}, ${rt}`, expr.line);
                    }
                    if (lt === "char" && rt === "char") return "int";
                    return promote(lt === "char" ? "int" : lt, rt === "char" ? "int" : rt);
                }

                // -, *, /, %
                if (!isNumericish(lt) || !isNumericish(rt)) {
                    throw new CodegenError(`Bad operand types for '${expr.op}': ${lt}, ${rt}`, expr.line);
                }
                if (lt === "char" && rt === "char") return "int";
                return promote(lt === "char" ? "int" : lt, rt === "char" ? "int" : rt);
            }
        }
    }

    private toDisplayString(expr: Expr, scope: Scope): string {
        const t = this.inferType(expr, scope);
        const code = this.genExpr(expr, scope);
        switch (t) {
            case "char":
                return `__fmtChar(${code})`;
            case "double":
                return `__fmtDouble(${code})`;
            case "String":
                return code;
            default:
                return `String(${code})`;
        }
    }

    private numericOperand(expr: Expr, scope: Scope): string {
        const t = this.inferType(expr, scope);
        const code = this.genExpr(expr, scope);
        if (t === "char") return `(${code}).charCodeAt(0)`;
        return code;
    }

    private genExpr(expr: Expr, scope: Scope): string {
        switch (expr.kind) {
            case "IntLiteral":
                return String(expr.value);
            case "DoubleLiteral":
                return String(expr.value);
            case "BooleanLiteral":
                return String(expr.value);
            case "CharLiteral":
                return JSON.stringify(expr.value);
            case "StringLiteral":
                return JSON.stringify(expr.value);
            case "Ident":
                return expr.name;
            case "Assign": {
                const declared = scope.get(expr.name);
                if (!declared) throw new CodegenError(`Cannot find variable '${expr.name}'`, expr.line);

                if (expr.op === "=") {
                    const value = this.coerceTo(expr.expr, declared, scope);
                    return `(${expr.name} = ${value})`;
                }

                const binOp = expr.op[0] as "+" | "-" | "*" | "/" | "%";
                const rhsType = this.inferType(expr.expr, scope);

                if (declared === "String") {
                    if (binOp !== "+") {
                        throw new CodegenError(`Bad operand type for '${expr.op}': String`, expr.line);
                    }
                    const rhsDisplay = this.toDisplayString(expr.expr, scope);
                    return `(${expr.name} = (${expr.name} + ${rhsDisplay}))`;
                }

                if (declared !== "int" && declared !== "long" && declared !== "float" && declared !== "double") {
                    throw new CodegenError(`Compound assignment is not supported for ${declared}`, expr.line);
                }
                if (!isNumericish(rhsType)) {
                    throw new CodegenError(
                        `Incompatible types: ${rhsType} cannot be converted to ${declared}`,
                        expr.line
                    );
                }

                const rhs = this.numericOperand(expr.expr, scope);
                const combined = this.wrapNumeric(declared, binOp, expr.name, rhs);
                return `(${expr.name} = ${combined})`;
            }
            case "Unary": {
                const inner = this.genExpr(expr.expr, scope);
                if (expr.op === "++" || expr.op === "--") {
                    return expr.prefix ? `${expr.op}${inner}` : `${inner}${expr.op}`;
                }
                return `${expr.op}${inner}`;
            }
            case "Logical": {
                const left = this.genExpr(expr.left, scope);
                const right = this.genExpr(expr.right, scope);
                const op = expr.op === "&&" ? "&&" : "||";
                return `(${left} ${op} ${right})`;
            }
            case "Ternary": {
                const cond = this.genExpr(expr.cond, scope);
                const a = this.genExpr(expr.whenTrue, scope);
                const b = this.genExpr(expr.whenFalse, scope);
                return `(${cond} ? ${a} : ${b})`;
            }
            case "Call": {
                const sig = this.methods.get(expr.callee);
                const args = expr.args
                    .map((a, idx) => (sig ? this.coerceTo(a, sig.paramTypes[idx], scope) : this.genExpr(a, scope)))
                    .join(", ");
                return `${expr.callee}(${args})`;
            }
            case "PrintCall": {
                const parts = expr.args.map((a) => this.toDisplayString(a, scope));
                const joined = parts.length > 0 ? parts.join(' + " " + ') : `""`;
                const text = expr.args.length > 0 ? joined : `""`;
                return expr.variant === "println" ? `__println(${text})` : `__print(${text})`;
            }
            case "SystemInRef":
                throw new CodegenError(
                    "'System.in' can only be used as the argument to 'new Scanner(...)'",
                    expr.line
                );
            case "NewObject": {
                this.inferType(expr, scope);
                return `__newScanner()`;
            }
            case "MethodCall": {
                this.inferType(expr, scope);
                const targetCode = this.genExpr(expr.target, scope);
                return `${targetCode}.${expr.method}()`;
            }
            case "Binary": {
                if (expr.op === "==" || expr.op === "!=" || expr.op === "<" || expr.op === "<=" ||
                    expr.op === ">" || expr.op === ">=") {
                    const lt = this.inferType(expr.left, scope);
                    const rt = this.inferType(expr.right, scope);
                    const left = lt === "char" || rt === "char" ? this.numericOperand(expr.left, scope) : this.genExpr(expr.left, scope);
                    const right = lt === "char" || rt === "char" ? this.numericOperand(expr.right, scope) : this.genExpr(expr.right, scope);
                    const jsOp = expr.op === "==" ? "===" : expr.op === "!=" ? "!==" : expr.op;
                    return `(${left} ${jsOp} ${right})`;
                }

                const resultType = this.inferType(expr, scope);
                if (expr.op === "+" && resultType === "String") {
                    const left = this.toDisplayString(expr.left, scope);
                    const right = this.toDisplayString(expr.right, scope);
                    return `(${left} + ${right})`;
                }

                const left = this.numericOperand(expr.left, scope);
                const right = this.numericOperand(expr.right, scope);
                return this.wrapNumeric(resultType, expr.op as "+" | "-" | "*" | "/" | "%", left, right);
            }
        }
    }

    private wrapNumeric(
        resultType: JavaType,
        op: "+" | "-" | "*" | "/" | "%",
        left: string,
        right: string
    ): string {
        if (op === "/") {
            if (resultType === "double") return `(${left} / ${right})`;
            return `__intDiv(${left}, ${right})`;
        }
        if (op === "%") {
            if (resultType === "double") return `(${left} % ${right})`;
            return `__intMod(${left}, ${right})`;
        }
        const raw = `(${left} ${op} ${right})`;
        if (resultType === "int") return `__int(${raw})`;
        return raw;
    }
}

export function generateJs(program: Program): string {
    return new Codegen().generate(program);
}
